/*!
 * TinyPoker 俱樂部配置管理組件
 * 提供俱樂部 CRUD 功能、超級代理、直屬代理管理
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import axios from "axios";
import classnames from "classnames";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
const API_BASE = "/api/tiny";

// 週 ID 定寬格式 YYYYMMDD-YYYYMMDD（前 8 碼即起始日，字串比較 = 時序比較）
// 放模組層：避免每次 render 重建，也讓 useMemo 的 deps 乾淨
const WEEK_ID_RE = /^\d{8}-\d{8}$/;

// ==================== 共用樣式常量 ====================
const COLORS = {
  cardBg: '#1e2530',
  headerBg: '#252d3a',
  inputBg: '#1a1f2b',
  border: 'rgba(255,255,255,0.1)',
  borderInput: 'rgba(255,255,255,0.2)',
  textPrimary: '#fff',
  textSecondary: 'rgba(255,255,255,0.8)',
  textMuted: 'rgba(255,255,255,0.5)',
  textDimmed: 'rgba(255,255,255,0.4)',
  accent: '#667eea',
  accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  dangerGradient: 'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
  success: '#2dce89',
  danger: '#f5365c',
  info: '#11cdef',
  warning: '#fb6340',
};

const inputStyle = {
  background: COLORS.inputBg,
  color: COLORS.textPrimary,
  border: `1px solid ${COLORS.borderInput}`,
  borderRadius: '6px',
};

const labelStyle = {
  color: COLORS.textSecondary,
  fontWeight: 500,
  fontSize: '13px',
};

const modalHeaderStyle = {
  background: COLORS.headerBg,
  color: COLORS.textPrimary,
  borderBottom: `1px solid ${COLORS.border}`,
};

const modalBodyStyle = {
  background: COLORS.cardBg,
  color: COLORS.textPrimary,
};

const modalFooterStyle = {
  background: COLORS.headerBg,
  borderTop: `1px solid ${COLORS.border}`,
};

const cancelBtnStyle = {
  background: 'rgba(149, 165, 166, 0.2)',
  border: '1px solid rgba(149, 165, 166, 0.5)',
  color: '#95a5a6',
  padding: '8px 20px',
  borderRadius: '6px',
};

const saveBtnStyle = (disabled) => ({
  background: disabled ? 'rgba(102, 126, 234, 0.5)' : COLORS.accentGradient,
  border: 'none',
  padding: '8px 30px',
  borderRadius: '6px',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
});

const thStyle = {
  borderColor: COLORS.border,
  padding: '10px 8px',
  fontWeight: 600,
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  borderColor: 'rgba(255,255,255,0.05)',
  padding: '10px 8px',
  verticalAlign: 'middle',
  // 窄螢幕不要把儲存格內容硬折成多行；欄位放不下時由外層容器水平捲動
  whiteSpace: 'nowrap',
};

// ==================== 顯示標籤映射 ====================
const TYPE_LABELS = { credit: '信用制', deposit: '儲值制' };
const STATUS_LABELS = { active: '啟用中', inactive: '已停用', deleted: '已刪除' };
const RATE_TYPE_LABELS = { fixed: '固定', conditional: '條件式', tiered: '階梯式' };
const RATE_TYPE_COLORS = {
  fixed: { bg: 'rgba(31, 142, 241, 0.15)', border: 'rgba(31, 142, 241, 0.4)', color: '#1f8ef1' },
  conditional: { bg: 'rgba(251, 99, 64, 0.15)', border: 'rgba(251, 99, 64, 0.4)', color: '#fb6340' },
  tiered: { bg: 'rgba(102, 126, 234, 0.15)', border: 'rgba(102, 126, 234, 0.4)', color: '#667eea' },
};

const getTypeLabel = (t) => TYPE_LABELS[t] || t;
const getStatusLabel = (s) => STATUS_LABELS[s] || s;
const getRateTypeLabel = (r) => RATE_TYPE_LABELS[r] || r;

// ==================== Enhanced Nav (ECOBOT 風格) ====================
const EnhancedNav = ({ children, ...props }) => (
  <Nav {...props} className="custom-nav mb-1 position-relative"
    style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', flexWrap: 'wrap', background: '#111822', padding: '4px', border: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
    {children}
  </Nav>
);
const EnhancedNavItem = ({ children, ...props }) => (
  <NavItem {...props} style={{ margin: '2px', position: 'relative', zIndex: 1 }}>{children}</NavItem>
);
// ⚠️ 原本這裡有一份 EnhancedNavLink —— **四份一模一樣的複製的最後一份**
//    （另三份在 TinyClubRake / TinyGameStats / TinyTopPlayers）。
//    已全部改用 components/tiny/TinyPageTabs。

// ==================== Section Card ====================
const SectionCard = ({ color, icon, title, children, style }) => (
  <div style={{ background: `rgba(${color}, 0.08)`, border: `1px solid rgba(${color}, 0.25)`, borderRadius: '8px', padding: '18px', marginBottom: '20px', ...style }}>
    {title && <h6 style={{ color: `rgb(${color})`, marginBottom: '15px', fontSize: '14px', fontWeight: 600 }}>{icon && <i className={`fa fa-${icon} mr-2`} />}{title}</h6>}
    {children}
  </div>
);

// ==================== 默認配置 ====================
const DEFAULT_CLUB = {
  club_id: "", club_name: "", type: "credit", status: "active",
  master_club_id: null,
  is_hq: false,                  // v2.2.0: 是否為本部俱樂部
  partner_id: null,              // v2.2.0: 合作夥伴 ID（譬如 'ecobot'）
  ecobot_role: null,             // v2.2.0: 'pure' | 'mixed' | null（在 partner 中的 club 角色）
  self_operated: false,          // v2.28.0: 我方自營的開桌機器人俱樂部（與 ecobot_role / partner_id 互斥）
  rates: { service_fee: 60, insurance: 60, mtt_profit: 40 },
  revenue_share_rule: null, remark: "",
  rebate_config: null,
  pool_mode: null,               // v2.17.0: 獎池回收模式，見 POOL_MODE_LABELS
  tr_config: null,               // v2.25.0: Tax & Rebate 歸屬，見 TR_MODES
  super_agents: [], direct_agents: [],
};

// v2.17.0 獎池回收模式：被回收的池歸我方聯盟，實發的池照常發俱樂部
const POOL_MODE_LABELS = {
  recover_only: "全回收（純分潤）",
  recover_activity: "活躍回收（輸退實發）",
  recover_loss_refund: "輸退回收（活躍實發）",
};

// v2.25.0 Tax & Rebate 歸屬（per-club 顯式設定，刻意不從 is_hq / pool_mode 等旗標推導 ——
// 同一個母俱底下的子俱可以有相反待遇，階層推導會出錯）
const TR_MODES = {
  DEFAULT: "default",   // tr_config = null → 跟隨全域預設規則
  DISABLED: "disabled", // tr_config = { enabled: false } → 不適用
  CUSTOM: "custom",     // tr_config = { enabled: true, rule_key } → 指定規則
};

function resolveTRMode(trConfig) {
  if (!trConfig) return TR_MODES.DEFAULT;
  if (trConfig.enabled === false) return TR_MODES.DISABLED;
  return trConfig.rule_key ? TR_MODES.CUSTOM : TR_MODES.DEFAULT;
}

// v2.45.1：「指定規則」但結束週已過（該週最後一天早於今天）→ 回傳結束週的 MMDD，否則 null。
// 只給列表標籤用；真正的判定在後端 tr_calculator.is_week_expired。
function trEndedMMDD(trConfig) {
  const until = trConfig?.effective_until_week;
  if (!until || until.length < 17) return null;
  const d = new Date();
  const today = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return until.slice(9, 17) < today ? until.slice(4, 8) : null;
}

const TR_SCOPE_NOTE = "僅計 Ring Game（NLH / PLO / AOF），不含 MTT / SNG";

const DEFAULT_AGENT = {
  id_field: "", nickname: "", rate_type: "fixed", base_rate: 60,
  bonus_rate: null, bonus_threshold: null, rule_key: "",
  split_to_others: null, exclude_pl_from_settlement: false,
  mtt_rate: 0, insurance_rate: 0, notes: "", is_active: true,
  is_ecobot: false,              // v2.2.0: 是否為 ecobot 超代/代理（標記本部底下的 ecobot 成員）
  rebate_config: null,
};

function TinyClubConfig() {
  const [clubs, setClubs] = useState([]);
  const [revenueRules, setRevenueRules] = useState([]);
  const [partners, setPartners] = useState([]);  // v2.2.0: 合作夥伴清單（partner_id dropdown 用）
  const [trRules, setTrRules] = useState([]);    // v2.25.0: TR 規則清單（rule_key dropdown 用）
  const [trSettings, setTrSettings] = useState(null);      // v2.25.0: TR 全域設定
  const [trSettingsDraft, setTrSettingsDraft] = useState(null);
  // v2.35.0：TR 全域設定從常駐大卡片改成「按鈕 → Modal」。
  // 先 view 再按編輯 —— 它是一頁裡最危險的設定（改生效週會改動整批帳），
  // 讓「看」跟「改」分成兩個動作，就不會滑一下就改到。
  const [trModalOpen, setTrModalOpen] = useState(false);
  const [trEditing, setTrEditing] = useState(false);
  const [trSavingSettings, setTrSavingSettings] = useState(false);
  const [weeks, setWeeks] = useState([]);        // v2.25.0: 生效起始週建議清單（非限制）
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterRebateMode, setFilterRebateMode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Club Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingClub, setEditingClub] = useState({ ...DEFAULT_CLUB });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  // Agent Modal (shared for super_agents & direct_agents)
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentModalType, setAgentModalType] = useState("super"); // "super" | "direct"
  const [agentModalMode, setAgentModalMode] = useState("add");
  const [editingAgent, setEditingAgent] = useState({ ...DEFAULT_AGENT });
  const [currentClubForAgent, setCurrentClubForAgent] = useState(null);

  // Delete Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clubToDelete, setClubToDelete] = useState(null);

  // ==================== 載入數據 ====================
  const loadClubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("type", filterType);
      if (filterStatus) params.append("status", filterStatus);
      const res = await axios.get(`${API_BASE}/clubs?${params.toString()}`);
      if (res.data.code === 1) setClubs(res.data.data.clubs || []);
      else throw new Error(res.data.msg);
    } catch (err) {
      setError("載入俱樂部失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  const loadRevenueRules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/revenue-rules`);
      if (res.data.code === 1) setRevenueRules(res.data.data.rules || []);
    } catch (err) { console.error("載入分潤規則失敗:", err); }
  };

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${API_BASE}/partners`);
      if (res.data.code === 1) setPartners(res.data.data.partners || []);
    } catch (err) { console.error("載入合作夥伴清單失敗:", err); }
  };

  // v2.25.0: TR 規則、全域設定、週期選單
  const loadTRRules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tr-rules`);
      if (res.data.code === 1) setTrRules(res.data.data.rules || []);
    } catch (err) { console.error("載入 TR 規則失敗:", err); }
  };

  const loadTRSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/system-settings/tr_settings`);
      const value = res.data.code === 1 ? res.data.data.value : null;
      const normalized = {
        enabled: false, default_rule_key: null, effective_from_week: null, ...(value || {}),
      };
      setTrSettings(normalized);
      setTrSettingsDraft(normalized);
    } catch (err) { console.error("載入 TR 全域設定失敗:", err); }
  };

  // v2.26.0：生效起始週是「往前指定」的設定，不該被既有資料的週期限制。
  // /data/weeks 讀的是 distinct('members_weekly','week') —— 還沒匯入的週不會出現，
  // 先前用 <select> 就變成「要先上傳資料才設得了未來週」，而且 DB 有值但選項沒有時
  // 畫面會顯示空白（看起來像沒設定）。改成自由輸入，下拉只當建議。
  const weekOptions = useMemo(() => {
    const list = [...weeks];
    const latest = list.length ? list.slice().sort().pop() : null;
    if (latest && WEEK_ID_RE.test(latest)) {
      const toDate = (v) => new Date(+v.slice(0, 4), +v.slice(4, 6) - 1, +v.slice(6, 8));
      const fmt = (d) => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
      let start = toDate(latest.split("-")[0]);
      for (let i = 0; i < 6; i += 1) {
        start = new Date(start.getTime() + 7 * 86400000);
        const end = new Date(start.getTime() + 6 * 86400000);
        list.push(`${fmt(start)}-${fmt(end)}`);
      }
    }
    return Array.from(new Set(list)).sort().reverse();
  }, [weeks]);

  const weekInputInvalid = (v) => !!v && !WEEK_ID_RE.test(String(v).trim());

  const loadWeeks = async () => {
    try {
      const res = await axios.get(`${API_BASE}/data/weeks`);
      if (res.data.code === 1) {
        const list = res.data.data.weeks || res.data.data || [];
        setWeeks(list.map((w) => (typeof w === "string" ? w : w.week)).filter(Boolean));
      }
    } catch (err) { console.error("載入週期清單失敗:", err); }
  };

  const saveTRSettings = async () => {
    setTrSavingSettings(true); setError(null);
    try {
      const res = await axios.put(`${API_BASE}/system-settings/tr_settings`, { value: trSettingsDraft });
      if (res.data.code === 1) { setSuccess("TR 全域設定已更新"); setTrSettings(trSettingsDraft); setTrEditing(false); }
      else throw new Error(res.data.msg);
    } catch (err) {
      setError("TR 全域設定保存失敗: " + (err.response?.data?.msg || err.message));
    } finally { setTrSavingSettings(false); }
  };

  useEffect(() => {
    loadClubs(); loadRevenueRules(); loadPartners();
    loadTRRules(); loadTRSettings(); loadWeeks();
  }, [loadClubs]);

  const filteredClubs = clubs.filter((club) => {
    if (filterRebateMode === "rebate" && club.rebate_config?.rate == null) return false;
    if (filterRebateMode === "pool" && (club.rebate_config?.rate != null || club.pool_mode)) return false;
    if (filterRebateMode === "pure_share" && !club.pool_mode) return false;
    // v2.25.0 TR 維度
    if (filterRebateMode === "tr_disabled" && resolveTRMode(club.tr_config) !== TR_MODES.DISABLED) return false;
    if (filterRebateMode === "tr_custom" && resolveTRMode(club.tr_config) !== TR_MODES.CUSTOM) return false;
    if (filterRebateMode === "tr_default" && resolveTRMode(club.tr_config) !== TR_MODES.DEFAULT) return false;
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return club.club_id?.toLowerCase().includes(t) || club.club_name?.toLowerCase().includes(t) || club.remark?.toLowerCase().includes(t);
  }).sort((a, b) => {
    // 主俱樂部 (master_club_id == null) 排前面，非主俱樂部排後面
    const aIsSub = a.master_club_id ? 1 : 0;
    const bIsSub = b.master_club_id ? 1 : 0;
    return aIsSub - bIsSub;
  });

  // ==================== 俱樂部 CRUD ====================
  // v2.45.1：新俱樂部預設「不適用 TR」，只寫在新增這個入口。
  // 不能改 DEFAULT_CLUB.tr_config —— handleEdit 也拿它墊底，DB 裡沒有 tr_config 欄位的
  // 俱樂部（TinyWorld 家族就是）打開編輯再按儲存，TR 會被悄悄關掉。
  const handleCreate = () => { setEditingClub({ ...DEFAULT_CLUB, tr_config: { enabled: false } }); setModalMode("create"); setActiveTab("1"); setModalOpen(true); };

  const handleEdit = (club) => {
    setEditingClub({ ...DEFAULT_CLUB, ...club, rates: { ...DEFAULT_CLUB.rates, ...club.rates } });
    setModalMode("edit"); setActiveTab("1"); setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const url = modalMode === "create" ? `${API_BASE}/clubs` : `${API_BASE}/clubs/${editingClub.club_id}`;
      const method = modalMode === "create" ? axios.post : axios.put;
      const res = await method(url, editingClub);
      if (res.data.code === 1) {
        setSuccess(modalMode === "create" ? "俱樂部創建成功！" : "俱樂部更新成功！");
        setModalOpen(false); loadClubs();
      } else throw new Error(res.data.msg);
    } catch (err) { setError("保存失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteClick = (club) => { setClubToDelete(club); setDeleteModalOpen(true); };

  const handleDelete = async (hard = false) => {
    if (!clubToDelete) return;
    setSaving(true);
    try {
      const url = hard ? `${API_BASE}/clubs/${clubToDelete.club_id}?hard=true` : `${API_BASE}/clubs/${clubToDelete.club_id}`;
      const res = await axios.delete(url);
      if (res.data.code === 1) {
        setSuccess(`俱樂部 ${clubToDelete.club_id} 已${hard ? "永久" : ""}刪除`);
        setDeleteModalOpen(false); setClubToDelete(null); loadClubs();
      } else throw new Error(res.data.msg);
    } catch (err) { setError("刪除失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  // ==================== 代理 CRUD (共用 super / direct) ====================
  const openAgentModal = (club, type, mode, agent = null) => {
    setCurrentClubForAgent(club);
    setAgentModalType(type);
    setAgentModalMode(mode);
    if (mode === "add") {
      setEditingAgent({ ...DEFAULT_AGENT });
    } else {
      setEditingAgent({ ...DEFAULT_AGENT, ...agent });
    }
    setAgentModalOpen(true);
  };

  const handleSaveAgent = async () => {
    setSaving(true);
    try {
      const club_id = currentClubForAgent.club_id;
      const isSuper = agentModalType === "super";
      const idField = isSuper ? "super_id" : "agent_id";
      const basePath = isSuper ? "super-agents" : "direct-agents";

      // 構建 agent data
      const agentData = { ...editingAgent };
      // 映射 id_field 到正確的 key
      agentData[idField] = agentData.id_field || agentData[idField];
      delete agentData.id_field;

      // 清理 null 與空值
      Object.keys(agentData).forEach(k => {
        if (agentData[k] === "" || agentData[k] === null) {
          if (!['is_active', 'exclude_pl_from_settlement'].includes(k)) {
            delete agentData[k];
          }
        }
      });

      if (agentModalMode === "add") {
        const res = await axios.post(`${API_BASE}/clubs/${club_id}/${basePath}`, agentData);
        if (res.data.code === 1) { setSuccess(`${isSuper ? '超級代理' : '直屬代理'}添加成功！`); setAgentModalOpen(false); loadClubs(); }
        else throw new Error(res.data.msg);
      } else {
        const agentId = agentData[idField];
        const res = await axios.put(`${API_BASE}/clubs/${club_id}/${basePath}/${agentId}`, agentData);
        if (res.data.code === 1) { setSuccess(`${isSuper ? '超級代理' : '直屬代理'}更新成功！`); setAgentModalOpen(false); loadClubs(); }
        else throw new Error(res.data.msg);
      }
    } catch (err) { setError("保存失敗: " + (err.response?.data?.msg || err.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteAgent = async (club, type, agentId) => {
    const label = type === "super" ? "超級代理" : "直屬代理";
    if (!window.confirm(`確定要移除${label} ${agentId}？`)) return;
    try {
      const basePath = type === "super" ? "super-agents" : "direct-agents";
      const res = await axios.delete(`${API_BASE}/clubs/${club.club_id}/${basePath}/${agentId}`);
      if (res.data.code === 1) { setSuccess(`${label}已移除`); loadClubs(); }
      else throw new Error(res.data.msg);
    } catch (err) { setError("刪除失敗: " + (err.response?.data?.msg || err.message)); }
  };

  // ==================== 輔助方法 ====================
  const updateClubField = (field, value) => setEditingClub(p => ({ ...p, [field]: value }));
  const updateClubRate = (f, v) => setEditingClub(p => ({ ...p, rates: { ...p.rates, [f]: v === "" ? null : Number(v) } }));
  const updateAgentField = (f, v) => setEditingAgent(p => ({ ...p, [f]: v }));

  // 當 clubs 更新時，同步刷新正在編輯的 editingClub（確保 Modal 內數據即時更新）
  useEffect(() => {
    if (modalOpen && modalMode === "edit" && editingClub.club_id) {
      const updated = clubs.find(c => c.club_id === editingClub.club_id);
      if (updated) {
        setEditingClub(prev => ({
          ...DEFAULT_CLUB,
          ...updated,
          rates: { ...DEFAULT_CLUB.rates, ...updated.rates },
        }));
      }
    }
  }, [clubs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); } }, [success]);

  // ==================== 渲染 Agent 表格 (共用) ====================
  const renderAgentTable = (club, agents, type) => {
    const isSuper = type === "super";
    const idKey = isSuper ? "super_id" : "agent_id";
    const label = isSuper ? "超級代理" : "直屬代理";

    if (!agents || agents.length === 0) return (
      <div className="text-center p-3">
        <i className="fa fa-user-plus" style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.12)', marginBottom: '10px', display: 'block' }} />
        <p style={{ color: COLORS.textMuted, fontSize: '13px', marginBottom: '10px' }}>尚未設定{label}</p>
        <Button size="sm" onClick={() => openAgentModal(club, type, "add")}
          style={{ background: 'rgba(45,206,137,0.15)', border: '1px solid rgba(45,206,137,0.4)', color: COLORS.success, padding: '4px 14px', borderRadius: '5px', fontSize: '12px' }}>
          <i className="fa fa-plus mr-1" />新增
        </Button>
      </div>
    );

    return (
      <div style={{ overflowX: 'auto' }}>
        <Table responsive size="sm" style={{ marginBottom: 0, color: COLORS.textSecondary, fontSize: '12px' }}>
          <thead style={{ background: COLORS.headerBg, color: COLORS.textPrimary }}>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>暱稱</th>
              <th style={thStyle}>分潤類型</th>
              <th style={thStyle}>分潤詳情</th>
              <th style={thStyle}>狀態</th>
              <th style={thStyle}>備註</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => {
              const rtc = RATE_TYPE_COLORS[a.rate_type] || RATE_TYPE_COLORS.fixed;
              return (
                <tr key={a[idKey]} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <td style={tdStyle}><strong style={{ color: COLORS.textPrimary }}>{a[idKey]}</strong></td>
                  <td style={tdStyle}>{a.nickname || '-'}</td>
                  <td style={tdStyle}>
                    <Badge style={{ background: rtc.bg, border: `1px solid ${rtc.border}`, color: rtc.color, padding: '3px 8px', fontSize: '10px', fontWeight: 600 }}>
                      {getRateTypeLabel(a.rate_type)}
                    </Badge>
                  </td>
                  <td style={tdStyle}>
                    {a.rate_type === 'fixed' && (
                      <span style={{ color: '#1f8ef1' }}>{a.base_rate ?? '-'}%</span>
                    )}
                    {a.rate_type === 'conditional' && (
                      <span>
                        <span style={{ color: '#1f8ef1' }}>{a.base_rate ?? '-'}%</span>
                        {a.bonus_rate && <span style={{ color: '#fb6340' }}> → {a.bonus_rate}% (≥{(a.bonus_threshold || 0).toLocaleString()})</span>}
                      </span>
                    )}
                    {a.rate_type === 'tiered' && (
                      <span style={{ color: COLORS.accent }}>
                        {revenueRules.find(r => r.rule_key === a.rule_key)?.rule_name || a.rule_key || '-'}
                      </span>
                    )}
                    {((a.mtt_rate || 0) > 0 || (a.insurance_rate || 0) > 0) && (
                      <div style={{ fontSize: '10px', color: COLORS.textMuted, marginTop: '2px' }}>
                        {(a.mtt_rate || 0) > 0 && <span style={{ color: '#fb6340' }}>MTT {a.mtt_rate}%</span>}
                        {(a.mtt_rate || 0) > 0 && (a.insurance_rate || 0) > 0 && ' / '}
                        {(a.insurance_rate || 0) > 0 && <span style={{ color: '#2dce89' }}>保險 {a.insurance_rate}%</span>}
                      </div>
                    )}
                    {a.split_to_others && Object.keys(a.split_to_others).length > 0 && (
                      <div style={{ fontSize: '10px', color: COLORS.textMuted, marginTop: '2px' }}>
                        分潤: {Object.entries(a.split_to_others).map(([k, v]) => `${k} ${v}%`).join(', ')}
                      </div>
                    )}
                    {a.rebate_config?.rate != null && (
                      <div style={{ fontSize: '10px', marginTop: '2px' }}>
                        <span style={{ color: '#9b51e0', fontWeight: 600 }}>
                          <i className="fa fa-exchange mr-1" />Rebate {a.rebate_config.rate}%
                        </span>
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <Badge style={{
                      background: a.is_active ? 'rgba(45,206,137,0.2)' : 'rgba(149,165,166,0.2)',
                      color: a.is_active ? COLORS.success : '#95a5a6',
                      padding: '3px 8px', fontSize: '10px', fontWeight: 600,
                      border: `1px solid ${a.is_active ? 'rgba(45,206,137,0.4)' : 'rgba(149,165,166,0.4)'}`,
                    }}>
                      {a.is_active ? '啟用' : '停用'}
                    </Badge>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: '150px' }}>
                    <small style={{ color: COLORS.textMuted }}>{a.notes?.substring(0, 30)}{a.notes?.length > 30 && '...'}</small>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <Button size="sm" onClick={() => openAgentModal(club, type, "edit", { ...a, id_field: a[idKey] })}
                      style={{ background: 'rgba(17,205,239,0.15)', border: '1px solid rgba(17,205,239,0.4)', color: COLORS.info, padding: '3px 8px', borderRadius: '4px', marginRight: '4px' }}>
                      <i className="fa fa-edit" />
                    </Button>
                    <Button size="sm" onClick={() => handleDeleteAgent(club, type, a[idKey])}
                      style={{ background: 'rgba(245,54,92,0.15)', border: '1px solid rgba(245,54,92,0.4)', color: COLORS.danger, padding: '3px 8px', borderRadius: '4px' }}>
                      <i className="fa fa-trash" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // ==================== 主畫面 Loading ====================
  if (loading && clubs.length === 0) {
    return (
      <div className="content">
        <TinyLoading text="載入中" variant="page" />
      </div>
    );
  }

  return (
    <div className="content">
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {success && <Alert color="success" toggle={() => setSuccess(null)}>{success}</Alert>}

      {/* ⚠️ 標題原本寫「俱樂部配置管理」，側欄寫「俱樂部管理」—— 統一用側欄那個。
          「新增俱樂部」是動作 → 放在 header 正下方靠右，不進 header。 */}
      <TinyPageHeader
        title="俱樂部管理"
        description="管理所有俱樂部配置、分潤比例及超級代理。"
      />

      {/* ⚠️ KPI 只是四個計數，不需要一張卡包一張卡。
             Brad 2026-09-10：「不要佔這麼高的 height」→ 換成公版的 TinyStatStrip。 */}
      <TinyStatStrip items={[
        { label: '俱樂部總數', icon: 'fa-building', color: '17, 205, 239', iconColor: COLORS.info, value: filteredClubs.length },
        { label: '啟用中', icon: 'fa-check-circle', color: '45, 206, 137', iconColor: COLORS.success, value: clubs.filter(c => c.status === 'active').length },
        { label: '信用制', icon: 'fa-credit-card', color: '31, 142, 241', iconColor: '#1f8ef1', value: clubs.filter(c => c.type === 'credit').length },
        { label: '儲值制', icon: 'fa-bank', color: '251, 99, 64', iconColor: COLORS.warning, value: clubs.filter(c => c.type === 'deposit').length },
      ]} style={{ marginBottom: 10 }} />

      {/* Tax & Rebate 全域設定的入口。
          ⚠️ 它**不能混進上面那排統計**（Brad 2026-09-10）—— 那排每一格都是
             唯讀的數字，混一顆可以按的進去，沒有人會知道它可以按。
          🔑 自成一列、但壓到 40px 上下，並且把「可以按」講清楚：
             整條是 <button>、hover 會亮、游標是 pointer、右邊有明確的
             「檢視 / 設定」＋ chevron。狀態直接寫在條上，不用打開才知道。 */}
      {trSettings && (
        <button
          type="button"
          onClick={() => { setTrSettingsDraft(trSettings); setTrEditing(false); setTrModalOpen(true); }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(114,124,245,0.18)';
            e.currentTarget.style.borderColor = 'rgba(114,124,245,0.75)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(114,124,245,0.09)';
            e.currentTarget.style.borderColor = trSettings.enabled ? 'rgba(114,124,245,0.5)' : COLORS.border;
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            textAlign: 'left', cursor: 'pointer',
            background: 'rgba(114,124,245,0.09)',
            border: `1px solid ${trSettings.enabled ? 'rgba(114,124,245,0.5)' : COLORS.border}`,
            borderRadius: 8, padding: '8px 14px', marginBottom: 18,
            transition: 'background 0.16s ease, border-color 0.16s ease',
          }}
        >
          <i className="fa fa-balance-scale" style={{ color: '#727cf5', fontSize: 15 }} />
          <span style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
            Tax &amp; Rebate 全域設定
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9, whiteSpace: 'nowrap',
            background: trSettings.enabled ? 'rgba(45,206,137,0.2)' : 'rgba(255,255,255,0.08)',
            color: trSettings.enabled ? COLORS.success : COLORS.textDimmed,
          }}>
            {trSettings.enabled ? '已啟用' : '未啟用'}
          </span>
          <span style={{ fontSize: 12, color: COLORS.textMuted, whiteSpace: 'nowrap',
                         overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {trSettings.effective_from_week ? `${trSettings.effective_from_week} 起生效` : '未設生效週'}
          </span>
          {/* 靠右的動作提示 —— 這一段就是「這條可以按」的訊號 */}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                         color: '#9aa2f7', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            檢視 / 設定
            <i className="fa fa-chevron-right" style={{ fontSize: 10 }} />
          </span>
        </button>
      )}

      {/* ==================== 列表卡片 ==================== */}
      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
            <CardTitle tag="h5" style={{ color: COLORS.textPrimary, marginBottom: 0, fontWeight: 600 }}>
              <i className="fa fa-list mr-2" style={{ color: COLORS.accent }} />俱樂部列表
            </CardTitle>
            {/* Brad 2026-09-10：「新增俱樂部」不再自己佔一行，
                跟刷新一起放在列表卡片右上角 —— 它作用的對象就是這張列表。 */}
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Button size="sm" onClick={loadClubs} disabled={loading}
                style={{ background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.4)', color: COLORS.accent, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
                {loading ? <TinyBusy light style={{ marginRight: '5px' }} /> : <i className="fa fa-refresh mr-1" />}刷新
              </Button>
              <Button size="sm" onClick={handleCreate}
                style={{ background: COLORS.accentGradient, border: 'none', padding: '6px 18px', borderRadius: '6px', fontWeight: 600 }}>
                <i className="fa fa-plus mr-1" />新增俱樂部
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody style={{ padding: '20px' }}>
          {/* 篩選器 */}
          <Row className="mb-3">
            <Col xs={12} sm={6} xl={3}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-tag mr-2" />類型篩選</Label>
                <Input type="select" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={inputStyle}>
                  <option value="">全部類型</option>
                  <option value="credit">信用制</option>
                  <option value="deposit">儲值制</option>
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-filter mr-2" />狀態篩選</Label>
                <Input type="select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={inputStyle}>
                  <option value="active">啟用中</option>
                  <option value="inactive">已停用</option>
                  <option value="deleted">已刪除</option>
                  <option value="all">全部狀態</option>
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-exchange mr-2" />模式篩選</Label>
                <Input type="select" value={filterRebateMode} onChange={(e) => setFilterRebateMode(e.target.value)} style={inputStyle}>
                  <option value="">全部模式</option>
                  <option value="pool">獎池模式</option>
                  <option value="rebate">Rebate 模式</option>
                  <option value="pure_share">回收模式（純分潤/活躍/輸退）</option>
                  <option value="tr_default">TR：跟隨全域預設</option>
                  <option value="tr_custom">TR：指定規則</option>
                  <option value="tr_disabled">TR：不適用</option>
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-search mr-2" />搜索</Label>
                <Input type="text" placeholder="搜索俱樂部 ID、名稱或備註..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={inputStyle} />
              </FormGroup>
            </Col>
          </Row>

          {/* 表格 */}
          {filteredClubs.length === 0 ? (
            <div className="text-center p-5">
              <i className="fa fa-inbox" style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.15)', marginBottom: '20px', display: 'block' }} />
              <p style={{ color: COLORS.textMuted, fontSize: '1.1rem' }}>沒有符合條件的俱樂部</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table responsive hover size="sm" style={{ marginBottom: 0, color: COLORS.textSecondary, minWidth: '1180px' }}>
                <thead style={{ background: COLORS.headerBg, color: COLORS.textPrimary }}>
                  <tr>
                    {['俱樂部 ID', '名稱', '類型', '狀態', '分潤計算', '分潤規則', '模式', '超級代理', '直屬代理', '備註', '操作'].map((h, i) => (
                      <th key={i} style={{ ...thStyle, ...(h === '操作' ? { textAlign: 'right' } : {}), fontSize: '13px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const mainClubs = filteredClubs.filter(c => !c.master_club_id);
                    const subClubs = filteredClubs.filter(c => c.master_club_id);
                    const renderClubRow = (club) => (
                      <tr key={club.club_id} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}><strong style={{ color: COLORS.textPrimary, fontSize: '14px' }}>{club.club_id}</strong></td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}><span style={{ fontWeight: 500 }}>{club.club_name}</span></td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          <Badge style={{ background: club.type === "credit" ? 'linear-gradient(135deg,#1f8ef1,#0d6efd)' : 'linear-gradient(135deg,#fb6340,#fbb140)', padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '4px' }}>
                            {getTypeLabel(club.type)}
                          </Badge>
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          <Badge style={{ background: club.status === "active" ? 'linear-gradient(135deg,#2dce89,#26a770)' : club.status === "inactive" ? 'rgba(149,165,166,0.3)' : 'linear-gradient(135deg,#f5365c,#f56036)', padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '4px' }}>
                            {getStatusLabel(club.status)}
                          </Badge>
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          {club.rates?.service_fee == null ? (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
                              <span style={{ background: 'rgba(255,193,7,0.15)', border: '1px solid rgba(255,193,7,0.4)', color: '#ffc107', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>依階梯規則</span>
                              <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
                              <span style={{ color: '#2dce89', fontSize: '13px', fontWeight: 600 }}>保險 {club.rates?.insurance ?? '-'}%</span>
                              <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
                              <span style={{ color: '#fb6340', fontSize: '13px', fontWeight: 600 }}>MTT {club.rates?.mtt_profit ?? '-'}%</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
                              <span style={{ color: '#1f8ef1', fontSize: '13px', fontWeight: 700 }}>服務費 {club.rates?.service_fee ?? '-'}%</span>
                              <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
                              <span style={{ color: '#2dce89', fontSize: '13px', fontWeight: 700 }}>保險 {club.rates?.insurance ?? '-'}%</span>
                              <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
                              <span style={{ color: '#fb6340', fontSize: '13px', fontWeight: 700 }}>MTT {club.rates?.mtt_profit ?? '-'}%</span>
                            </div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          {club.rates?.service_fee == null ? (
                            <span style={{ background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.3)', color: '#ffc107', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                              <i className="fa fa-line-chart mr-1" style={{ fontSize: '10px' }} />
                              {revenueRules.find(r => r.rule_key === club.revenue_share_rule)?.rule_name || club.revenue_share_rule || '未設定'}
                            </span>
                          ) : (
                            <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>固定分潤百分比</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          {club.rebate_config?.rate != null ? (
                            <Badge style={{ background: 'linear-gradient(135deg,#9b51e0,#7b2cbf)', color: '#fff', padding: '4px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '4px' }}>
                              <i className="fa fa-exchange mr-1" style={{ fontSize: '9px' }} />
                              Rebate {club.rebate_config.rate}%
                            </Badge>
                          ) : club.pool_mode ? (
                            <Badge style={{ background: 'linear-gradient(135deg,#e8a33d,#c77f1a)', color: '#fff', padding: '4px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '4px' }}>
                              <i className="fa fa-percent mr-1" style={{ fontSize: '9px' }} />
                              {POOL_MODE_LABELS[club.pool_mode] || club.pool_mode}
                            </Badge>
                          ) : (
                            <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>獎池</span>
                          )}
                          {club.self_operated && (
                            <div style={{ marginTop: '4px' }}>
                              <Badge style={{ background: 'linear-gradient(135deg,#00d6b4,#0d9488)', color: '#fff', padding: '3px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '4px' }}>
                                <i className="fa fa-android mr-1" style={{ fontSize: '9px' }} />自營 Bot
                              </Badge>
                            </div>
                          )}
                          {/* v2.25.0 TR 歸屬（與上方獎池/Rebate 模式並存，兩者互不影響） */}
                          {(() => {
                            const mode = resolveTRMode(club.tr_config);
                            if (mode === TR_MODES.DISABLED) {
                              return (
                                <div style={{ marginTop: '4px' }}>
                                  <Badge style={{ background: 'rgba(255,255,255,0.06)', color: COLORS.textDimmed, padding: '3px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '4px' }}>
                                    TR 不適用
                                  </Badge>
                                </div>
                              );
                            }
                            if (mode === TR_MODES.CUSTOM) {
                              const ruleName = trRules.find(r => r.rule_key === club.tr_config.rule_key)?.rule_name
                                || club.tr_config.rule_key;
                              const endedMMDD = trEndedMMDD(club.tr_config);
                              if (endedMMDD) {
                                const { effective_from_week: from, effective_until_week: until } = club.tr_config;
                                return (
                                  <div style={{ marginTop: '4px' }}>
                                    <Badge title={`${ruleName}：${from || '沿用全域起始週'} ~ ${until}`} style={{ background: 'rgba(255,255,255,0.06)', color: COLORS.textDimmed, padding: '3px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '4px' }}>
                                      TR 已結束（至 {endedMMDD} 週）
                                    </Badge>
                                  </div>
                                );
                              }
                              return (
                                <div style={{ marginTop: '4px' }}>
                                  <Badge title={TR_SCOPE_NOTE} style={{ background: 'linear-gradient(135deg,#727cf5,#5a63d8)', color: '#fff', padding: '3px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '4px' }}>
                                    <i className="fa fa-balance-scale mr-1" style={{ fontSize: '9px' }} />
                                    TR {ruleName}
                                  </Badge>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          <Badge style={{ background: 'rgba(102,126,234,0.2)', color: COLORS.accent, padding: '4px 10px', fontSize: '12px', fontWeight: 600, marginRight: '6px' }}>
                            {club.super_agents?.filter(a => a.is_active !== false).length || 0}
                            <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>/{club.super_agents?.length || 0}</span>
                          </Badge>
                          <Button size="sm" className="p-0" onClick={() => openAgentModal(club, "super", "add")} title="添加超級代理"
                            style={{ background: 'transparent', border: 'none', color: COLORS.success, fontSize: '14px' }}>
                            <i className="fa fa-plus-circle" />
                          </Button>
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          <Badge style={{ background: 'rgba(251,99,64,0.2)', color: COLORS.warning, padding: '4px 10px', fontSize: '12px', fontWeight: 600, marginRight: '6px' }}>
                            {club.direct_agents?.filter(a => a.is_active !== false).length || 0}
                            <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>/{club.direct_agents?.length || 0}</span>
                          </Badge>
                          <Button size="sm" className="p-0" onClick={() => openAgentModal(club, "direct", "add")} title="添加直屬代理"
                            style={{ background: 'transparent', border: 'none', color: COLORS.success, fontSize: '14px' }}>
                            <i className="fa fa-plus-circle" />
                          </Button>
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px' }}>
                          <small style={{ color: COLORS.textMuted }}>{club.remark?.substring(0, 20)}{club.remark?.length > 20 && "..."}</small>
                        </td>
                        <td style={{ ...tdStyle, padding: '12px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Button size="sm" onClick={() => handleEdit(club)} title="編輯"
                            style={{ background: 'rgba(17,205,239,0.15)', border: '1px solid rgba(17,205,239,0.4)', color: COLORS.info, padding: '6px 12px', borderRadius: '5px', marginRight: '6px' }}>
                            <i className="fa fa-edit" />
                          </Button>
                          <Button size="sm" onClick={() => handleDeleteClick(club)} title="刪除"
                            style={{ background: 'rgba(245,54,92,0.15)', border: '1px solid rgba(245,54,92,0.4)', color: COLORS.danger, padding: '6px 12px', borderRadius: '5px' }}>
                            <i className="fa fa-trash" />
                          </Button>
                        </td>
                      </tr>
                    );
                    return (
                      <>
                        {mainClubs.map(renderClubRow)}
                        {subClubs.length > 0 && (
                          <tr key="__separator__">
                            <td colSpan={10} style={{ padding: '0', border: 'none' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 10px 8px', borderTop: '2px solid rgba(102,126,234,0.3)' }}>
                                <i className="fa fa-sitemap" style={{ color: COLORS.accent, fontSize: '13px' }} />
                                <span style={{ color: COLORS.accent, fontSize: '12px', fontWeight: 600, letterSpacing: '1px' }}>非主俱樂部（子俱樂部）</span>
                                <div style={{ flex: 1, height: '1px', background: 'rgba(102,126,234,0.2)' }} />
                              </div>
                            </td>
                          </tr>
                        )}
                        {subClubs.map(renderClubRow)}
                      </>
                    );
                  })()}
                </tbody>
              </Table>
            </div>
          )}
          <div style={{ marginTop: '15px', color: COLORS.textMuted, fontSize: '13px' }}>
            共 {filteredClubs.length} 個俱樂部 {loading && <TinyBusy text="更新中" style={{ marginLeft: 8 }} />}
          </div>
        </CardBody>
      </Card>

      {/* ==================== Tax & Rebate 全域設定 Modal（v2.35.0） ====================
          原本是列表上方一張常駐大卡片，佔掉整整一排卻九成時間不會動它。
          🔑 **先 view 再 edit**：改「生效起始週」等於改動一整批週期的帳
             （見 Backend/CLAUDE.md「生效閘門」），所以要多按一次「編輯」才動得了。
          關掉 Modal 一律把 draft 丟回 trSettings —— 沒按儲存就等於沒改。 */}
      <Modal isOpen={trModalOpen} toggle={() => { setTrModalOpen(false); setTrSettingsDraft(trSettings); setTrEditing(false); }}
        size="lg" contentClassName="tiny-modal-content">
        <ModalHeader toggle={() => { setTrModalOpen(false); setTrSettingsDraft(trSettings); setTrEditing(false); }}
          style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <span style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
            <i className="fa fa-balance-scale mr-2" style={{ color: '#727cf5' }} />
            Tax &amp; Rebate 全域設定
            <Badge style={{
              marginLeft: '10px',
              background: trSettingsDraft?.enabled ? 'rgba(45,206,137,0.2)' : 'rgba(255,255,255,0.08)',
              color: trSettingsDraft?.enabled ? COLORS.success : COLORS.textDimmed,
              padding: '4px 10px', fontSize: '11px', fontWeight: 700,
            }}>
              {trSettingsDraft?.enabled ? '已啟用' : '尚未啟用'}
            </Badge>
            {!trEditing && (
              <span style={{ marginLeft: 10, fontSize: 11, color: COLORS.textMuted, fontWeight: 400 }}>唯讀</span>
            )}
          </span>
        </ModalHeader>
        <ModalBody style={{ background: COLORS.cardBg, padding: '20px' }}>
          {trSettingsDraft && (trEditing ? (
            <Row>
              <Col xs={12} sm={6}>
                <FormGroup>
                  <Label style={labelStyle}><i className="fa fa-power-off mr-2" />總開關</Label>
                  <Input type="select" value={trSettingsDraft.enabled ? "1" : "0"}
                    onChange={(e) => setTrSettingsDraft(p => ({ ...p, enabled: e.target.value === "1" }))}
                    style={inputStyle}>
                    <option value="0">停用（完全不計算 TR）</option>
                    <option value="1">啟用</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col xs={12} sm={6}>
                <FormGroup>
                  <Label style={labelStyle}><i className="fa fa-sitemap mr-2" />全域預設規則</Label>
                  <Input type="select" value={trSettingsDraft.default_rule_key || ""}
                    onChange={(e) => setTrSettingsDraft(p => ({ ...p, default_rule_key: e.target.value || null }))}
                    style={inputStyle}>
                    <option value="">（未設定）</option>
                    {trRules.map(r => <option key={r.rule_key} value={r.rule_key}>{r.rule_name}</option>)}
                  </Input>
                  <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>沒有個別指定規則的俱樂部一律套用這條</small>
                </FormGroup>
              </Col>
              <Col xs={12}>
                <FormGroup>
                  <Label style={labelStyle}><i className="fa fa-calendar mr-2" />生效起始週</Label>
                  <Input type="text" list="tr-week-options"
                    placeholder="YYYYMMDD-YYYYMMDD（留空 = 不生效）"
                    value={trSettingsDraft.effective_from_week || ""}
                    onChange={(e) => setTrSettingsDraft(p => ({ ...p, effective_from_week: e.target.value || null }))}
                    style={{
                      ...inputStyle,
                      borderColor: weekInputInvalid(trSettingsDraft.effective_from_week)
                        ? '#f5365c' : inputStyle.borderColor,
                    }} />
                  <datalist id="tr-week-options">
                    {weekOptions.map(w => <option key={w} value={w} />)}
                  </datalist>
                  <small style={{
                    color: weekInputInvalid(trSettingsDraft.effective_from_week)
                      ? '#f5365c' : COLORS.textMuted, fontSize: '11px' }}>
                    {weekInputInvalid(trSettingsDraft.effective_from_week)
                      ? '格式錯誤，需為 YYYYMMDD-YYYYMMDD'
                      : '只有這一週（含）之後的週期才算 TR；可直接輸入尚未匯入的未來週期'}
                  </small>
                </FormGroup>
              </Col>
            </Row>
          ) : (
            <Row>
              {[
                { label: '總開關', icon: 'fa-power-off',
                  value: trSettingsDraft.enabled ? '啟用' : '停用（完全不計算 TR）' },
                { label: '全域預設規則', icon: 'fa-sitemap',
                  value: (trRules.find(r => r.rule_key === trSettingsDraft.default_rule_key) || {}).rule_name || '（未設定）',
                  hint: '沒有個別指定規則的俱樂部一律套用這條' },
                { label: '生效起始週', icon: 'fa-calendar',
                  value: trSettingsDraft.effective_from_week || '（未設定 → 不生效）',
                  hint: '只有這一週（含）之後的週期才算 TR' },
              ].map((f) => (
                <Col xs={12} sm={6} key={f.label}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ ...labelStyle, marginBottom: 4 }}>
                      <i className={`fa ${f.icon} mr-2`} />{f.label}
                    </div>
                    <div style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: 14 }}>{f.value}</div>
                    {f.hint && <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>{f.hint}</small>}
                  </div>
                </Col>
              ))}
            </Row>
          ))}
          <div style={{ padding: '10px 14px', background: 'rgba(114,124,245,0.10)', border: '1px solid rgba(114,124,245,0.4)', borderRadius: '6px' }}>
            <small style={{ color: '#9aa2f7', fontSize: '12px' }}>
              <i className="fa fa-info-circle mr-1" />
              重跑起始週<strong>之前</strong>的舊週期不會補算 TR，帳不會被回頭改動。
              設定改完後跑該週 pipeline 才會反映到 Excel 與各頁面。
            </small>
          </div>
        </ModalBody>
        <ModalFooter style={{ background: COLORS.headerBg, borderTop: `1px solid ${COLORS.border}` }}>
          {trEditing ? (
            <>
              <Button size="sm" color="secondary" outline
                onClick={() => { setTrSettingsDraft(trSettings); setTrEditing(false); }}>
                取消編輯
              </Button>
              <Button size="sm" onClick={saveTRSettings} disabled={trSavingSettings}
                style={{ background: COLORS.accentGradient, border: 'none', padding: '6px 20px', borderRadius: '6px', fontWeight: 600 }}>
                {trSavingSettings ? <><TinyBusy light style={{ marginRight: 4 }} />儲存中</> : <><i className="fa fa-check mr-1" />儲存設定</>}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" color="secondary" outline
                onClick={() => { setTrModalOpen(false); setTrSettingsDraft(trSettings); }}>
                關閉
              </Button>
              <Button size="sm" onClick={() => setTrEditing(true)}
                style={{ background: COLORS.accentGradient, border: 'none', padding: '6px 20px', borderRadius: '6px', fontWeight: 600 }}>
                <i className="fa fa-pencil mr-1" />編輯
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* ==================== 俱樂部 Modal ==================== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" style={{ marginTop: '30px' }}>
        <ModalHeader toggle={() => setModalOpen(false)} style={modalHeaderStyle}>
          <i className={`fa fa-${modalMode === "create" ? "plus-circle" : "edit"} mr-2`} style={{ color: COLORS.accent }} />
          {modalMode === "create" ? "新增俱樂部" : `編輯俱樂部 - ${editingClub.club_id}`}
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {error && <Alert color="danger" toggle={() => setError(null)} style={{ marginBottom: '12px' }}>{error}</Alert>}
          {success && <Alert color="success" toggle={() => setSuccess(null)} style={{ marginBottom: '12px' }}>{success}</Alert>}

          {/* ⚠️ 這組 tab 在 Modal 裡（不是頁面層級），但刻意用同一支公版 ——
              目的就是視覺語言統一；元件名叫 PageTabs 只是命名沒跟上。 */}
          <TinyPageTabs
            items={[
              { key: "1", label: "基本資訊", icon: "fa fa-info-circle" },
              { key: "2", label: "分潤設定", icon: "fa fa-percent" },
              ...(modalMode === "edit"
                ? [
                    { key: "3", label: "超級代理", icon: "fa fa-user-secret",
                      count: editingClub.super_agents?.length || 0 },
                    { key: "4", label: "直屬代理", icon: "fa fa-user",
                      count: editingClub.direct_agents?.length || 0 },
                  ]
                : []),
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />

          <TabContent activeTab={activeTab} style={{ paddingTop: '20px' }}>
            {/* 基本資訊 */}
            <TabPane tabId="1">
              <SectionCard color="102, 126, 234" icon="info-circle" title="俱樂部資訊">
                <Row>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}>俱樂部 ID <span style={{ color: COLORS.danger }}>*</span></Label>
                    <Input type="text" value={editingClub.club_id} onChange={(e) => updateClubField("club_id", e.target.value.replace(/[\t\n\r]/g, '').trim())} disabled={modalMode === "edit"} placeholder="例如: 717512" style={{ ...inputStyle, ...(modalMode === "edit" ? { opacity: 0.6 } : {}) }} />
                  </FormGroup></Col>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}>俱樂部名稱 <span style={{ color: COLORS.danger }}>*</span></Label>
                    <Input type="text" value={editingClub.club_name} onChange={(e) => updateClubField("club_name", e.target.value)} placeholder="例如: TiNY POKER C" style={inputStyle} />
                  </FormGroup></Col>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}>母俱樂部 ID <small style={{ color: COLORS.textMuted, fontWeight: 400 }}>(子俱樂部才填)</small></Label>
                    <Input type="text" value={editingClub.master_club_id || ""} onChange={(e) => {
                      const val = e.target.value.trim();
                      updateClubField("master_club_id", val || null);
                    }} placeholder="留空=獨立俱樂部" style={inputStyle} />
                  </FormGroup></Col>
                </Row>
              </SectionCard>
              <SectionCard color="45, 206, 137" icon="cog" title="配置選項">
                <Row>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}>類型 <span style={{ color: COLORS.danger }}>*</span></Label>
                    <Input type="select" value={editingClub.type} onChange={(e) => updateClubField("type", e.target.value)} style={inputStyle}>
                      <option value="credit">信用制</option><option value="deposit">儲值制</option>
                    </Input>
                  </FormGroup></Col>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}>狀態</Label>
                    <Input type="select" value={editingClub.status} onChange={(e) => updateClubField("status", e.target.value)} style={inputStyle}>
                      <option value="active">啟用中</option><option value="inactive">已停用</option>
                    </Input>
                  </FormGroup></Col>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}>分潤計算方式</Label>
                    <Input type="select" value={editingClub.rates?.service_fee == null ? "tiered" : "fixed"}
                      onChange={(e) => {
                        if (e.target.value === "tiered") {
                          setEditingClub(p => ({ ...p, rates: { ...p.rates, service_fee: null } }));
                        } else {
                          setEditingClub(p => ({ ...p, rates: { ...p.rates, service_fee: 60 } }));
                        }
                      }} style={inputStyle}
                    >
                      <option value="fixed">固定費率</option>
                      <option value="tiered">走階梯規則</option>
                    </Input>
                  </FormGroup></Col>
                </Row>
                {/* 階梯規則選擇（階梯模式） */}
                {editingClub.rates?.service_fee == null && (
                  <Row>
                    <Col md="6"><FormGroup>
                      <Label style={labelStyle}><i className="fa fa-line-chart mr-1" style={{ color: COLORS.accent }} />階梯規則</Label>
                      <Input type="select" value={editingClub.revenue_share_rule || ""}
                        onChange={(e) => updateClubField("revenue_share_rule", e.target.value)}
                        style={inputStyle}>
                        <option value="">-- 選擇規則 --</option>
                        {revenueRules.map(r => <option key={r.rule_key} value={r.rule_key}>{r.rule_name || r.rule_key}</option>)}
                      </Input>
                      <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>服務費將依據當周總水自動查表計算</small>
                    </FormGroup></Col>
                  </Row>
                )}
              </SectionCard>
              <SectionCard color="91, 158, 219" icon="sitemap" title="分類設定 (v2.2.0 / v2.28.0)">
                <Row>
                  <Col md="3"><FormGroup>
                    <Label style={labelStyle}>本部俱樂部</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
                      <Input
                        type="checkbox"
                        checked={!!editingClub.is_hq}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          // is_hq 跟 partner_id 互斥；勾起本部時自動清 partner_id（保留 ecobot_role 因為本部可以是 mixed）
                          if (checked) {
                            setEditingClub(p => ({ ...p, is_hq: true, partner_id: null }));
                          } else {
                            updateClubField("is_hq", false);
                          }
                        }}
                        style={{ position: 'static', margin: 0 }}
                      />
                      <small style={{ color: COLORS.textMuted }}>勾選=本部 (HQ)；下次 pipeline 跑後生效</small>
                    </div>
                  </FormGroup></Col>
                  {/* v2.28.0 自營 Bot：我方自己研發的開桌機器人。聯盟全額承擔它的 rake + 輸贏（含 JP），
                      營收頁會有獨立一條「自營Bot 損益」。與 EcoBot 完全不同 —— EcoBot 是外部夥伴、
                      我方只分潤；自營 Bot 等於聯盟自己下場。兩者互斥（後端 clubs_service 有擋）。 */}
                  <Col md="3"><FormGroup>
                    <Label style={labelStyle}>自營 Bot 俱樂部</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
                      <Input
                        type="checkbox"
                        checked={!!editingClub.self_operated}
                        disabled={!!editingClub.partner_id || !!editingClub.ecobot_role}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            // 自營 Bot 是我方的，不可能同時屬於外部夥伴 / 是 EcoBot
                            setEditingClub(p => ({ ...p, self_operated: true, partner_id: null, ecobot_role: null }));
                          } else {
                            updateClubField("self_operated", false);
                          }
                        }}
                        style={{ position: 'static', margin: 0, ...((editingClub.partner_id || editingClub.ecobot_role) ? { opacity: 0.4 } : {}) }}
                      />
                      <small style={{ color: COLORS.textMuted }}>
                        {(editingClub.partner_id || editingClub.ecobot_role)
                          ? '已屬合作夥伴 / EcoBot，不可同時是自營 Bot'
                          : '聯盟全額承擔 rake + 輸贏(含JP)'}
                      </small>
                    </div>
                  </FormGroup></Col>
                  <Col md="3"><FormGroup>
                    <Label style={labelStyle}>合作夥伴 {editingClub.is_hq && <small style={{ color: COLORS.textMuted, fontWeight: 400 }}>(本部不可設)</small>}{editingClub.self_operated && <small style={{ color: COLORS.textMuted, fontWeight: 400 }}>(自營Bot不可設)</small>}</Label>
                    <Input type="select" value={editingClub.partner_id || ""} onChange={(e) => {
                      const v = e.target.value || null;
                      updateClubField("partner_id", v);
                      // 清掉 partner 時，若 ecobot_role='pure' 也清（pure 必依附 partner）；mixed 可以保留（本部 mixed 場景）
                      if (!v && editingClub.ecobot_role === 'pure') updateClubField("ecobot_role", null);
                    }} disabled={!!editingClub.is_hq || !!editingClub.self_operated} style={{ ...inputStyle, ...((editingClub.is_hq || editingClub.self_operated) ? { opacity: 0.5 } : {}) }}>
                      <option value="">— 無 —</option>
                      {partners.map(p => <option key={p.partner_id} value={p.partner_id}>{p.partner_name || p.partner_id}</option>)}
                    </Input>
                  </FormGroup></Col>
                  <Col md="3"><FormGroup>
                    <Label style={labelStyle}>EcoBot Club 角色</Label>
                    <Input type="select" value={editingClub.ecobot_role || ""} onChange={(e) => updateClubField("ecobot_role", e.target.value || null)}
                      disabled={!!editingClub.self_operated}
                      style={{ ...inputStyle, ...(editingClub.self_operated ? { opacity: 0.5 } : {}) }}>
                      <option value="">— 不適用（純真人）—</option>
                      <option value="pure" disabled={!editingClub.partner_id}>純 EcoBot{!editingClub.partner_id ? ' (需先選合作夥伴)' : ''}</option>
                      <option value="mixed">混合（含 ecobot 內容）</option>
                    </Input>
                    <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>
                      本部 mixed 表示「club 內藏 ecobot 超代/玩家」
                    </small>
                  </FormGroup></Col>
                </Row>
              </SectionCard>
              <SectionCard color="251, 99, 64" icon="pencil" title="備註">
                <FormGroup style={{ marginBottom: 0 }}>
                  <Input type="textarea" rows="3" value={editingClub.remark} onChange={(e) => updateClubField("remark", e.target.value)} placeholder="輸入備註..." style={inputStyle} />
                </FormGroup>
              </SectionCard>
            </TabPane>

            {/* 分潤設定 */}
            <TabPane tabId="2">
              {/* 計算模式提示 */}
              {editingClub.rates?.service_fee == null ? (
                <div style={{ background: 'rgba(102,126,234,0.1)', border: '1px solid rgba(102,126,234,0.3)', borderRadius: '8px', padding: '14px 18px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <i className="fa fa-line-chart" style={{ color: COLORS.accent, fontSize: '18px' }} />
                  <div>
                    <div style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: '13px' }}>
                      <Badge style={{ background: 'rgba(102,126,234,0.25)', color: COLORS.accent, padding: '3px 10px', fontSize: '11px', fontWeight: 600, marginRight: '8px' }}>階梯模式</Badge>
                      服務費由當周總水查表自動計算
                    </div>
                    <div style={{ color: COLORS.textMuted, fontSize: '12px', marginTop: '4px' }}>
                      規則: {revenueRules.find(r => r.rule_key === editingClub.revenue_share_rule)?.rule_name || editingClub.revenue_share_rule || '未設定'}
                      {editingClub.master_club_id && <span> / 母俱樂部: {editingClub.master_club_id}</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ background: 'rgba(31,142,241,0.1)', border: '1px solid rgba(31,142,241,0.3)', borderRadius: '8px', padding: '14px 18px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <i className="fa fa-lock" style={{ color: '#1f8ef1', fontSize: '18px' }} />
                  <div>
                    <div style={{ color: COLORS.textPrimary, fontWeight: 600, fontSize: '13px' }}>
                      <Badge style={{ background: 'rgba(31,142,241,0.25)', color: '#1f8ef1', padding: '3px 10px', fontSize: '11px', fontWeight: 600, marginRight: '8px' }}>固定模式</Badge>
                      所有費率皆為固定百分比
                    </div>
                  </div>
                </div>
              )}

              <SectionCard color="31, 142, 241" icon="sliders" title="分潤百分比配置">
                <Row>
                  {/* 服務費 */}
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}><i className="fa fa-dollar mr-1" style={{ color: '#1f8ef1' }} />服務費比例 (%)</Label>
                    {editingClub.rates?.service_fee == null ? (
                      <div>
                        <Input type="text" value="依階梯規則自動計算" disabled style={{ ...inputStyle, opacity: 0.6, fontStyle: 'italic' }} />
                        <small style={{ color: COLORS.accent, fontSize: '11px', marginTop: '5px', display: 'block' }}>
                          <i className="fa fa-info-circle mr-1" />報表產出時依當周總水查表
                        </small>
                      </div>
                    ) : (
                      <div>
                        <InputGroup>
                          <Input type="number" min="0" max="100" value={editingClub.rates?.service_fee ?? ""} onChange={(e) => updateClubRate('service_fee', e.target.value)} style={inputStyle} />
                          <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                        </InputGroup>
                        <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>俱樂部服務費分潤比例</small>
                      </div>
                    )}
                  </FormGroup></Col>
                  {/* 保險 */}
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}><i className="fa fa-shield mr-1" style={{ color: '#2dce89' }} />保險比例 (%)</Label>
                    <InputGroup>
                      <Input type="number" min="0" max="100" value={editingClub.rates?.insurance ?? ""} onChange={(e) => updateClubRate('insurance', e.target.value)} style={inputStyle} />
                      <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                    </InputGroup>
                    <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>保險費用分潤比例</small>
                  </FormGroup></Col>
                  {/* MTT */}
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}><i className="fa fa-trophy mr-1" style={{ color: '#fb6340' }} />MTT 獲利比例 (%)</Label>
                    <InputGroup>
                      <Input type="number" min="0" max="100" value={editingClub.rates?.mtt_profit ?? ""} onChange={(e) => updateClubRate('mtt_profit', e.target.value)} style={inputStyle} />
                      <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                    </InputGroup>
                    <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>MTT 獲利分潤比例</small>
                  </FormGroup></Col>
                </Row>
              </SectionCard>

              <SectionCard color="155, 81, 224" icon="exchange" title="獎池處理模式（可選）">
                <Row>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}><i className="fa fa-percent mr-1" style={{ color: '#9b51e0' }} />Rebate 比例 (%)</Label>
                    <InputGroup>
                      <Input type="number" min="0" max="100" value={editingClub.rebate_config?.rate ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          // Rebate 與獎池回收模式互斥：啟用 Rebate 時自動清 pool_mode
                          setEditingClub(p => ({
                            ...p,
                            rebate_config: v === "" ? null : { rate: Number(v) },
                            pool_mode: v === "" ? p.pool_mode : null,
                          }));
                        }}
                        disabled={!!editingClub.pool_mode}
                        placeholder="留空 = 獎池模式" style={{ ...inputStyle, ...(editingClub.pool_mode ? { opacity: 0.5 } : {}) }} />
                      <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                    </InputGroup>
                    <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>
                      留空 = 走獎池模式（預設）；填數字啟用 Rebate 模式（公式：Rebate% × abs(負水前輸贏)，扣 MTT/SNG/JP）
                    </small>
                  </FormGroup></Col>
                  <Col md="4"><FormGroup>
                    <Label style={labelStyle}><i className="fa fa-hand mr-1" style={{ color: '#e8a33d' }} />獎池回收模式 {editingClub.rebate_config?.rate != null && <small style={{ color: COLORS.textMuted, fontWeight: 400 }}>(Rebate 模式不可設)</small>}</Label>
                    <Input type="select" value={editingClub.pool_mode || ""}
                      onChange={(e) => {
                        const v = e.target.value || null;
                        // 獎池回收模式與 Rebate 互斥：啟用時自動清 rebate_config
                        setEditingClub(p => ({
                          ...p,
                          pool_mode: v,
                          rebate_config: v ? null : p.rebate_config,
                        }));
                      }}
                      disabled={editingClub.rebate_config?.rate != null}
                      style={{ ...inputStyle, ...(editingClub.rebate_config?.rate != null ? { opacity: 0.5 } : {}) }}>
                      <option value="">一般（兩池達標都實發）</option>
                      <option value="recover_activity">活躍回收（輸退實發、活躍由我方回收）</option>
                      <option value="recover_loss_refund">輸退回收（活躍實發、輸退由我方回收）</option>
                      <option value="recover_only">全回收 — 純分潤（無rebate無獎池）</option>
                    </Input>
                    <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>
                      獎池照算，被回收的池歸我方聯盟、實發的池照常發放。設在母俱樂部才有效（母+子合併計算）
                    </small>
                  </FormGroup></Col>
                  <Col md="4">
                    {editingClub.rebate_config?.rate != null && (
                      <div style={{ marginTop: '28px', padding: '10px 14px', background: 'rgba(155,81,224,0.10)', border: '1px solid rgba(155,81,224,0.4)', borderRadius: '6px' }}>
                        <small style={{ color: '#c79afc', fontSize: '12px' }}>
                          <i className="fa fa-info-circle mr-1" />
                          Rebate 模式啟用：俱樂部 Excel 不顯示獎池 sheet；本部仍照常算雙週獎池，應分金額由本部回收。
                        </small>
                      </div>
                    )}
                    {editingClub.pool_mode && (
                      <div style={{ marginTop: '28px', padding: '10px 14px', background: 'rgba(232,163,61,0.10)', border: '1px solid rgba(232,163,61,0.4)', borderRadius: '6px' }}>
                        <small style={{ color: '#e8a33d', fontSize: '12px' }}>
                          <i className="fa fa-info-circle mr-1" />
                          {POOL_MODE_LABELS[editingClub.pool_mode]}啟用：
                          {editingClub.pool_mode === "recover_only"
                            ? "俱樂部 Excel 不顯示獎池 sheet；兩池應分金額全由我方聯盟回收（不發放、不退水）。"
                            : "被回收的池由我方聯盟回收、實發的池照常發放並顯示在俱樂部 Excel。"}
                          模式異動請在雙週期開始前完成，Week 1 / Week 2 不一致時系統會擋報表。
                        </small>
                      </div>
                    )}
                  </Col>
                </Row>
              </SectionCard>

              {/* ==================== Tax & Rebate（v2.25.0） ==================== */}
              <SectionCard color="114, 124, 245" icon="balance-scale" title="Tax & Rebate (TR)" style={{ marginBottom: 0 }}>
                <div style={{ marginBottom: '15px' }}>
                  <small style={{ color: COLORS.textMuted, fontSize: '12px' }}>
                    依 <strong style={{ color: COLORS.textSecondary }}>ratio = 水前輸贏（扣除 JP）÷ Ring Game 總水</strong> 決定補貼或抽成，
                    疊加在分潤與雙週獎池之上（獎池照常發放，不受影響）。{TR_SCOPE_NOTE}。
                    {!trSettings?.enabled && (
                      <span style={{ color: COLORS.warning }}>
                        {" "}目前 TR 全域尚未啟用，此處設定會先存下來、等啟用後才生效。
                      </span>
                    )}
                  </small>
                </div>
                <Row>
                  <Col md="5"><FormGroup>
                    <Label style={labelStyle}>本俱樂部的 TR 歸屬</Label>
                    <Input type="select" value={resolveTRMode(editingClub.tr_config)}
                      onChange={(e) => {
                        const mode = e.target.value;
                        setEditingClub(p => {
                          if (mode === TR_MODES.DEFAULT) return { ...p, tr_config: null };
                          if (mode === TR_MODES.DISABLED) return { ...p, tr_config: { enabled: false } };
                          return {
                            ...p,
                            tr_config: {
                              enabled: true,
                              rule_key: p.tr_config?.rule_key || trSettings?.default_rule_key || (trRules[0]?.rule_key ?? ""),
                              effective_from_week: p.tr_config?.effective_from_week || null,
                              effective_until_week: p.tr_config?.effective_until_week || null,
                            },
                          };
                        });
                      }}
                      style={inputStyle}>
                      <option value={TR_MODES.DEFAULT}>跟隨全域預設規則</option>
                      <option value={TR_MODES.DISABLED}>不適用 TR（維持現有機制）</option>
                      <option value={TR_MODES.CUSTOM}>指定規則</option>
                    </Input>
                    <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>
                      每個俱樂部各自設定，不會沿母／子俱樂部傳染
                    </small>
                  </FormGroup></Col>

                  {resolveTRMode(editingClub.tr_config) === TR_MODES.CUSTOM && (
                    <>
                      <Col md="4"><FormGroup>
                        <Label style={labelStyle}>TR 規則</Label>
                        <Input type="select" value={editingClub.tr_config?.rule_key || ""}
                          onChange={(e) => setEditingClub(p => ({
                            ...p, tr_config: { ...p.tr_config, enabled: true, rule_key: e.target.value },
                          }))}
                          style={inputStyle}>
                          <option value="">請選擇規則</option>
                          {trRules.map(r => <option key={r.rule_key} value={r.rule_key}>{r.rule_name}</option>)}
                        </Input>
                        <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>
                          級距在「TR 規則」頁維護
                        </small>
                      </FormGroup></Col>
                    </>
                  )}
                </Row>

                {resolveTRMode(editingClub.tr_config) === TR_MODES.CUSTOM && (
                  <Row>
                    <Col md="5"><FormGroup>
                      <Label style={labelStyle}>專屬生效起始週</Label>
                      <Input type="text" list="tr-week-options"
                        placeholder="YYYYMMDD-YYYYMMDD（留空 = 沿用全域）"
                        value={editingClub.tr_config?.effective_from_week || ""}
                        onChange={(e) => setEditingClub(p => ({
                          ...p, tr_config: { ...p.tr_config, enabled: true, effective_from_week: e.target.value || null },
                        }))}
                        style={{
                          ...inputStyle,
                          borderColor: weekInputInvalid(editingClub.tr_config?.effective_from_week)
                            ? '#f5365c' : inputStyle.borderColor,
                        }} />
                    </FormGroup></Col>
                    <Col md="5"><FormGroup>
                      <Label style={labelStyle}>專屬生效結束週</Label>
                      <Input type="text" list="tr-week-options"
                        placeholder="YYYYMMDD-YYYYMMDD（留空 = 無上界）"
                        value={editingClub.tr_config?.effective_until_week || ""}
                        onChange={(e) => setEditingClub(p => ({
                          ...p, tr_config: { ...p.tr_config, enabled: true, effective_until_week: e.target.value || null },
                        }))}
                        style={{
                          ...inputStyle,
                          borderColor: weekInputInvalid(editingClub.tr_config?.effective_until_week)
                            ? '#f5365c' : inputStyle.borderColor,
                        }} />
                      <small style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '5px', display: 'block' }}>
                        <strong style={{ color: COLORS.textSecondary }}>含這一週</strong>；設了就不必事後手動關掉，
                        重跑任何一週都會得到同一個答案
                      </small>
                    </FormGroup></Col>
                  </Row>
                )}

                {/* 提醒而非阻擋：要不要豁免由人決定，規則改了不用回頭改 validation */}
                {(editingClub.is_hq || editingClub.partner_id || editingClub.rebate_config?.rate != null)
                  && resolveTRMode(editingClub.tr_config) !== TR_MODES.DISABLED && (
                  <div style={{ padding: '10px 14px', background: 'rgba(251,99,64,0.10)', border: '1px solid rgba(251,99,64,0.4)', borderRadius: '6px' }}>
                    <small style={{ color: '#fb6340', fontSize: '12px' }}>
                      <i className="fa fa-exclamation-triangle mr-1" />
                      這家俱樂部是
                      {editingClub.is_hq && " 本部"}
                      {editingClub.partner_id && ` 合作夥伴（${editingClub.partner_id}）`}
                      {editingClub.rebate_config?.rate != null && ` 已有固定 Rebate ${editingClub.rebate_config.rate}%`}
                      ，通常不套用 TR（會重複給付／內部沖銷）。確定要套用請忽略此提醒。
                    </small>
                  </div>
                )}
              </SectionCard>
            </TabPane>

            {/* 超級代理 Tab */}
            {modalMode === "edit" && (
              <TabPane tabId="3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 style={{ color: COLORS.textSecondary, marginBottom: 0, fontWeight: 600 }}>
                    <i className="fa fa-user-secret mr-2" style={{ color: COLORS.accent }} />超級代理列表
                  </h6>
                  <Button size="sm" onClick={() => openAgentModal(editingClub, "super", "add")}
                    style={{ background: 'rgba(45,206,137,0.15)', border: '1px solid rgba(45,206,137,0.4)', color: COLORS.success, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
                    <i className="fa fa-plus mr-1" /> 添加超級代理
                  </Button>
                </div>
                {renderAgentTable(editingClub, editingClub.super_agents, "super")}
              </TabPane>
            )}

            {/* 直屬代理 Tab */}
            {modalMode === "edit" && (
              <TabPane tabId="4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 style={{ color: COLORS.textSecondary, marginBottom: 0, fontWeight: 600 }}>
                    <i className="fa fa-user mr-2" style={{ color: COLORS.warning }} />直屬代理列表
                  </h6>
                  <Button size="sm" onClick={() => openAgentModal(editingClub, "direct", "add")}
                    style={{ background: 'rgba(45,206,137,0.15)', border: '1px solid rgba(45,206,137,0.4)', color: COLORS.success, padding: '6px 16px', borderRadius: '6px', fontWeight: 500 }}>
                    <i className="fa fa-plus mr-1" /> 添加直屬代理
                  </Button>
                </div>
                {renderAgentTable(editingClub, editingClub.direct_agents, "direct")}
              </TabPane>
            )}
          </TabContent>
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
            {saving ? <><TinyBusy light style={{ marginRight: 8 }} />儲存中</> : <><i className="fa fa-check mr-2" />保存</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ==================== 代理 Modal (共用) ==================== */}
      <Modal isOpen={agentModalOpen} toggle={() => setAgentModalOpen(false)} size="lg" style={{ marginTop: '50px' }}>
        <ModalHeader toggle={() => setAgentModalOpen(false)} style={modalHeaderStyle}>
          <i className={`fa fa-${agentModalMode === "add" ? "user-plus" : "user"} mr-2`} style={{ color: agentModalType === "super" ? COLORS.accent : COLORS.warning }} />
          {agentModalMode === "add" ? "添加" : "編輯"}{agentModalType === "super" ? "超級代理" : "直屬代理"}
          {currentClubForAgent && <small style={{ color: COLORS.textMuted, marginLeft: '10px', fontSize: '12px' }}>({currentClubForAgent.club_name})</small>}
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {error && <Alert color="danger" toggle={() => setError(null)} style={{ marginBottom: '12px' }}>{error}</Alert>}
          {success && <Alert color="success" toggle={() => setSuccess(null)} style={{ marginBottom: '12px' }}>{success}</Alert>}
          {/* 基本資訊 */}
          <SectionCard color="102, 126, 234" icon="id-card" title="基本資訊">
            <Row>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}>{agentModalType === "super" ? "Super ID" : "Agent ID"} <span style={{ color: COLORS.danger }}>*</span></Label>
                <Input type="text" value={editingAgent.id_field || ""} onChange={(e) => updateAgentField("id_field", e.target.value)}
                  disabled={agentModalMode === "edit"} placeholder="例如: 1234-5678" style={{ ...inputStyle, ...(agentModalMode === "edit" ? { opacity: 0.6 } : {}) }} />
              </FormGroup></Col>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}>暱稱</Label>
                <Input type="text" value={editingAgent.nickname || ""} onChange={(e) => updateAgentField("nickname", e.target.value)} placeholder="例如: jrscout" style={inputStyle} />
              </FormGroup></Col>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}>狀態</Label>
                <Input type="select" value={editingAgent.is_active === false ? "false" : "true"} onChange={(e) => updateAgentField("is_active", e.target.value === "true")} style={inputStyle}>
                  <option value="true">啟用</option>
                  <option value="false">停用</option>
                </Input>
              </FormGroup></Col>
            </Row>
            <Row>
              <Col md="12"><FormGroup style={{ marginBottom: 0 }}>
                <Label style={labelStyle}>EcoBot 標記 (v2.2.0)</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '4px' }}>
                  <Input type="checkbox" checked={!!editingAgent.is_ecobot} onChange={(e) => updateAgentField("is_ecobot", e.target.checked)} style={{ position: 'static', margin: 0 }} />
                  <small style={{ color: COLORS.textMuted }}>勾選=此{agentModalType === "super" ? "超代" : "直代"}屬於 EcoBot 合作夥伴；下次 pipeline 跑後生效</small>
                </div>
              </FormGroup></Col>
            </Row>
          </SectionCard>

          {/* 分潤類型 */}
          <SectionCard color="31, 142, 241" icon="sliders" title="分潤配置">
            <Row>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}>分潤類型 <span style={{ color: COLORS.danger }}>*</span></Label>
                <Input type="select" value={editingAgent.rate_type || "fixed"} onChange={(e) => updateAgentField("rate_type", e.target.value)} style={inputStyle}>
                  <option value="fixed">固定 (Fixed)</option>
                  <option value="conditional">條件式 (Conditional)</option>
                  <option value="tiered">階梯式 (Tiered)</option>
                </Input>
              </FormGroup></Col>

              {/* Fixed / Conditional 共用 base_rate */}
              {(editingAgent.rate_type === 'fixed' || editingAgent.rate_type === 'conditional') && (
                <Col md="4"><FormGroup>
                  <Label style={labelStyle}>基礎分潤 (%)</Label>
                  <InputGroup>
                    <Input type="number" min="0" max="100" value={editingAgent.base_rate ?? ""} onChange={(e) => updateAgentField("base_rate", e.target.value === "" ? null : Number(e.target.value))} style={inputStyle} />
                    <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                  </InputGroup>
                </FormGroup></Col>
              )}

              {/* Tiered: rule_key */}
              {editingAgent.rate_type === 'tiered' && (
                <Col md="8"><FormGroup>
                  <Label style={labelStyle}>分潤規則 <span style={{ color: COLORS.danger }}>*</span></Label>
                  <Input type="select" value={editingAgent.rule_key || ""} onChange={(e) => updateAgentField("rule_key", e.target.value)} style={inputStyle}>
                    <option value="">-- 選擇規則 --</option>
                    {revenueRules.map(r => <option key={r.rule_key} value={r.rule_key}>{r.rule_name || r.rule_key}</option>)}
                  </Input>
                </FormGroup></Col>
              )}
            </Row>

            {/* Conditional 額外欄位 */}
            {editingAgent.rate_type === 'conditional' && (
              <Row>
                <Col md="4"><FormGroup>
                  <Label style={labelStyle}>加碼分潤 (%)</Label>
                  <InputGroup>
                    <Input type="number" min="0" max="100" value={editingAgent.bonus_rate ?? ""} onChange={(e) => updateAgentField("bonus_rate", e.target.value === "" ? null : Number(e.target.value))} style={inputStyle} />
                    <InputGroupText style={{ background: COLORS.inputBg, color: COLORS.textMuted, border: `1px solid ${COLORS.borderInput}` }}>%</InputGroupText>
                  </InputGroup>
                </FormGroup></Col>
                <Col md="4"><FormGroup>
                  <Label style={labelStyle}>達標門檻 (周水)</Label>
                  <Input type="number" min="0" value={editingAgent.bonus_threshold ?? ""} onChange={(e) => updateAgentField("bonus_threshold", e.target.value === "" ? null : Number(e.target.value))} placeholder="例如: 150000" style={inputStyle} />
                </FormGroup></Col>
                <Col md="4"><FormGroup>
                  <Label style={labelStyle}>排除損益交收</Label>
                  <Input type="select" value={editingAgent.exclude_pl_from_settlement ? "true" : "false"} onChange={(e) => updateAgentField("exclude_pl_from_settlement", e.target.value === "true")} style={inputStyle}>
                    <option value="false">否</option>
                    <option value="true">是</option>
                  </Input>
                </FormGroup></Col>
              </Row>
            )}

            {/* 通用額外費率 */}
            <Row>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}><span style={{ color: '#2dce89' }}>保險分潤 (%)</span></Label>
                <Input type="number" min="0" max="100" value={editingAgent.insurance_rate ?? 0} onChange={(e) => updateAgentField("insurance_rate", e.target.value === "" ? 0 : Number(e.target.value))} placeholder="預設 0%" style={inputStyle} />
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>預設 0%，僅特殊情況才需填寫</small>
              </FormGroup></Col>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}><span style={{ color: '#fb6340' }}>MTT 分潤 (%)</span></Label>
                <Input type="number" min="0" max="100" value={editingAgent.mtt_rate ?? 0} onChange={(e) => updateAgentField("mtt_rate", e.target.value === "" ? 0 : Number(e.target.value))} placeholder="預設 0%" style={inputStyle} />
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>預設 0%，僅特殊情況才需填寫</small>
              </FormGroup></Col>
              <Col md="4"><FormGroup>
                <Label style={labelStyle}><span style={{ color: '#9b51e0' }}>Rebate (%)</span></Label>
                <Input type="number" min="0" max="100"
                  value={editingAgent.rebate_config?.rate ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateAgentField("rebate_config", v === "" ? null : { rate: Number(v) });
                  }}
                  placeholder="留空 = 不啟用" style={inputStyle} />
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>僅本部直營意義；填數字啟用</small>
              </FormGroup></Col>
            </Row>

            {/* Fixed 特殊: split_to_others */}
            {editingAgent.rate_type === 'fixed' && (
              <div style={{ marginTop: '10px' }}>
                <Label style={labelStyle}>分潤給其他人 (JSON 格式，選填)</Label>
                <Input type="text" value={editingAgent.split_to_others ? JSON.stringify(editingAgent.split_to_others) : ""} placeholder='例如: {"大泓": 5}'
                  onChange={(e) => {
                    try {
                      const val = e.target.value ? JSON.parse(e.target.value) : null;
                      updateAgentField("split_to_others", val);
                    } catch { /* ignore parse error while typing */ }
                  }} style={inputStyle} />
                <small style={{ color: COLORS.textMuted, fontSize: '11px' }}>格式: {`{"名稱": 百分比}`}，例如: {`{"大泓": 5}`} 表示分 5% 給大泓</small>
              </div>
            )}
          </SectionCard>

          {/* 備註 */}
          <SectionCard color="251, 99, 64" icon="pencil" title="備註" style={{ marginBottom: 0 }}>
            <FormGroup style={{ marginBottom: 0 }}>
              <Input type="textarea" rows="2" value={editingAgent.notes || ""} onChange={(e) => updateAgentField("notes", e.target.value)} placeholder="輸入備註..." style={inputStyle} />
            </FormGroup>
          </SectionCard>
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setAgentModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={handleSaveAgent} disabled={saving} style={saveBtnStyle(saving)}>
            {saving ? <><TinyBusy light style={{ marginRight: 8 }} />儲存中</> : <><i className="fa fa-check mr-2" />保存</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ==================== 刪除確認 Modal ==================== */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} style={{ marginTop: '100px' }}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)} style={modalHeaderStyle}>
          <i className="fa fa-exclamation-triangle mr-2" style={{ color: COLORS.danger }} />確認刪除
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          {error && <Alert color="danger" toggle={() => setError(null)} style={{ marginBottom: '12px' }}>{error}</Alert>}
          {clubToDelete && (
            <div>
              <div style={{ background: 'rgba(245,54,92,0.1)', border: '1px solid rgba(245,54,92,0.3)', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                <p style={{ marginBottom: 0, color: COLORS.textSecondary }}>
                  <i className="fa fa-warning mr-2" style={{ color: COLORS.danger }} />
                  確定要刪除俱樂部 <strong style={{ color: COLORS.textPrimary }}>{clubToDelete.club_id}</strong> ({clubToDelete.club_name}) 嗎？
                </p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                <div style={{ marginBottom: '8px' }}><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>類型：</strong>
                  <Badge style={{ background: clubToDelete.type === "credit" ? 'linear-gradient(135deg,#1f8ef1,#0d6efd)' : 'linear-gradient(135deg,#fb6340,#fbb140)', marginLeft: '10px', padding: '4px 10px', fontSize: '11px' }}>{getTypeLabel(clubToDelete.type)}</Badge></div>
                <div style={{ marginBottom: '8px' }}><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>超級代理：</strong><span style={{ marginLeft: '10px' }}>{clubToDelete.super_agents?.length || 0} 個</span></div>
                <div><strong style={{ color: COLORS.textSecondary, fontSize: '13px' }}>直屬代理：</strong><span style={{ marginLeft: '10px' }}>{clubToDelete.direct_agents?.length || 0} 個</span></div>
              </div>
              <div style={{ background: 'rgba(251,99,64,0.08)', border: '1px solid rgba(251,99,64,0.2)', borderRadius: '8px', padding: '15px' }}>
                <div style={{ marginBottom: '8px' }}><Badge style={{ background: 'rgba(251,99,64,0.3)', color: '#fb6340', padding: '4px 10px', fontSize: '11px' }}>軟刪除</Badge><span style={{ marginLeft: '10px', color: COLORS.textMuted, fontSize: '13px' }}>將狀態設為已刪除，可以恢復</span></div>
                <div><Badge style={{ background: 'rgba(245,54,92,0.3)', color: '#f5365c', padding: '4px 10px', fontSize: '11px' }}>永久刪除</Badge><span style={{ marginLeft: '10px', color: COLORS.textMuted, fontSize: '13px' }}>從數據庫中完全移除，無法恢復</span></div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button onClick={() => setDeleteModalOpen(false)} style={cancelBtnStyle}>取消</Button>
          <Button onClick={() => handleDelete(false)} disabled={saving}
            style={{ background: 'linear-gradient(135deg,#fb6340,#fbb140)', border: 'none', padding: '8px 20px', borderRadius: '6px', fontWeight: 600, boxShadow: '0 4px 12px rgba(251,99,64,0.3)' }}>
            <i className="fa fa-archive mr-2" />軟刪除
          </Button>
          <Button onClick={() => handleDelete(true)} disabled={saving}
            style={{ background: COLORS.dangerGradient, border: 'none', padding: '8px 20px', borderRadius: '6px', fontWeight: 600, boxShadow: '0 4px 12px rgba(245,54,92,0.3)' }}>
            <i className="fa fa-trash mr-2" />永久刪除
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TinyClubConfig;
